<script setup lang="ts">
/**
 * "Who is this seat for?" — the seat purchase surface (PRD §7.3, §7.7,
 * §9.2, §9.3, §11.1.8..§11.1.10, §11.2.4).
 *
 * **One product, one question.** Buying for yourself and buying for someone
 * else produce the same seat with the same entitlement — that is the point
 * of the seat model — so this is a single form with a holder choice, not two
 * CTAs and not a separate "gift" flow. The only thing the answer changes is
 * whether `holder_email` is sent.
 *
 * **Two response modes, one endpoint.** A payer with no billing agreement
 * needs Stripe to collect a card, so the server answers `mode: "checkout"`
 * and we redirect. A payer who already has one gets the seat added to that
 * same subscription, prorated (§7.7: one payer, one invoice), so the server
 * answers `mode: "seat_added"` and there is nothing to redirect to. Treating
 * the second as the first is a silent failure to the buyer, so the response
 * type is a union discriminated on `mode` and `url` is unreachable without
 * narrowing.
 *
 * **Funding someone else is always possible; naming them here is not** —
 * see `canNameHolder`. A payer with no subscription of their own buys the
 * seat unassigned and names the holder afterwards with the copy-link, so
 * the address never has to cross the Stripe redirect. The matching server
 * refusal is still handled for the tab whose plan lapsed while it sat open.
 *
 * **The promotion code is per seat, not per payer** (§8.3). The server
 * pre-applies it as a discount on *this seat's* line item rather than on the
 * subscription, so a payer funding several seats can put a different code on
 * each and still receive one invoice. Nothing here may assume one code per
 * payer; today's surface buys one seat at a time, so it collects one code at
 * a time.
 *
 * Stripe's redemption limits are per-code and per-CUSTOMER, never per-holder.
 * Three DIFFERENT codes across a payer's three seats therefore always work,
 * while the SAME code three times may be refused on the second. That refusal
 * arrives as `invalid_promo_code` — indistinguishable from a typo — so the
 * copy on the field names both causes. A payer who reads "invalid" for a code
 * they know is real concludes our field is broken.
 *
 * **Refusals are sentences, not a shrug.** Each reason code from §9.1/§11.1
 * says what happened and what to do next. `holder_already_entitled` is the
 * careful one — see REFUSAL_HOLDER_ENTITLED.
 *
 * **Two mount points, one surface** (§4.2). `StartPlanPanel` is the slide-over
 * this composes for, and Account renders the same component inline in a card
 * of its own. The layout is written for both: the foot pins to the bottom of
 * a 460px panel and simply sits last in a card. Everything §4.2 asks for lives
 * here rather than in the wrapper, so the panel and Account cannot describe
 * the same purchase differently.
 */
import { ArrowRight, EyeOff, Link as LinkIcon } from "lucide-vue-next";
import { computed, ref, useId } from "vue";
import { useRouter } from "vue-router";

import { apiClient, ApiError } from "@/api/client";
import type { SubscriptionStatus } from "@/api/types";
import AskForFunding from "@/components/AskForFunding.vue";
import RlSegmented from "@/components/ui/RlSegmented.vue";
import { REFUSAL_HOLDER_ENTITLED } from "@/lib/entitlement";
import { ACTION_LABELS, renewalLabel } from "@/lib/gettingStarted";
import { useAuthStore } from "@/stores/auth";

const props = defineProps<{
  /** The funding agreement's status from `GET /me/subscription`. */
  subscriptionStatus: SubscriptionStatus["status"];
}>();

const emit = defineEmits<{ purchased: [] }>();

const router = useRouter();
const auth = useAuthStore();

type Holder = "me" | "someone_else";

const HOLDER_OPTIONS = [
  { value: "me", label: "Me" },
  { value: "someone_else", label: "Someone else" },
] as const;

/**
 * Whether the invitee's address can be collected **here**, before payment.
 *
 * Not whether this account can fund someone else — since `rl-757.42` it
 * always can. The two paths differ in what they can carry, not in what
 * they allow:
 *
 * * With a live agreement, §7.7 adds an item to it synchronously. No
 *   Stripe redirect happens, so the address never leaves our systems and
 *   the seat is created named.
 * * Without one, the purchase goes through Checkout. An address could
 *   only survive that redirect inside Stripe metadata, disclosing a third
 *   party to a payment processor for a purpose outside DPIA P4 —
 *   so the seat is bought **unassigned** and named afterwards with the
 *   copy-link. The server refuses a named holder on this path
 *   (`funding_requires_existing_subscription`), which is why the field is
 *   hidden rather than merely ignored.
 *
 * Matched to the server's rule exactly — `active` or `trialing`. A
 * `past_due` or `cancel_at_period_end` agreement still entitles its holder
 * but does not fund anything new, so it cannot take a named seat either.
 */
const canNameHolder = computed(
  () =>
    props.subscriptionStatus === "active" ||
    props.subscriptionStatus === "trialing",
);

// §11.1.9 + §11.2.4's refusal lives in `lib/entitlement` (rl-757.28): the
// neutrality it is written for is a requirement rather than a nicety, and
// `FundedSeats`'s add-another-seat control needs the same sentence. See the
// constant for why a second copy here would be a liability.

// Reachable only when a plan lapsed between the page loading and the
// buyer submitting, since naming a holder is the one thing that needs
// one. Our sequencing, not the buyer's mistake, so it does not read as
// one — and it points at the route that still works.
const REFUSAL_NEEDS_OWN_PLAN =
  "Naming the person up front works by adding the seat to a plan of your own, and " +
  "the plan on this account is no longer active. Nothing has been charged. You can " +
  "still buy the seat and send them a link once it is paid for.";

// A buyer who already has a plan is, by elimination, buying for someone
// else: they hold their own seat already. A first-time buyer is not, so
// the default follows the same reasoning rather than the same value.
const holder = ref<Holder>(canNameHolder.value ? "someone_else" : "me");
const holderEmail = ref("");
const promoCode = ref("");
const promoOfferId = useId();
const submitting = ref(false);

const errorMessage = ref<string | null>(null);
/** `email_not_verified` needs somewhere to go: /verify resends the link. */
const showVerifyLink = ref(false);
/**
 * §9.5's ask, offered in place of the purchase the account cannot make.
 *
 * Kept out of `refusalIsFinal`'s reasoning on purpose: the CTA still goes
 * (buying again would be refused again), but the surface is not left with
 * nothing to do, which is the actual point of §4.2's rule.
 */
const showAskForFunding = ref(false);
/**
 * `invalid_promo_code` belongs on the field that caused it, not in the
 * panel's alert: it is the one refusal the buyer can fix in place, and a
 * generic "could not buy that seat" hides both the cause and the fix.
 */
const promoError = ref<string | null>(null);
/** Set only on `mode: "seat_added"`; null holder email means the buyer. */
const added = ref<{ holderEmail: string | null } | null>(null);
/**
 * Whether the message above the CTA ends this attempt, which decides whether
 * §4.2's refusal-in-place-of-the-CTA applies.
 *
 * For the §9.1 reason codes it does: none of them can be got past by pressing
 * the button again, so leaving it there invites a second identical refusal.
 * The unknown-code fallback says "try again in a moment" and the address
 * check asks for an address, so both keep the button they tell the buyer to
 * press — a refusal that removes the control its own copy names is the dead
 * end §10 forbids.
 */
const refusalIsFinal = ref(false);

const buyingForSelf = computed(() => holder.value === "me");

/**
 * §4.2's "First charge / Today, then {date}".
 *
 * A forecast of Stripe's monthly anchor, not a `current_period_end`: there is
 * no period to report before the first charge, which is why §5's
 * never-the-sentinel rule has nothing to say here. Stripe clamps the anchor
 * to the last day of a shorter month, so a seat bought on 31 January bills
 * again on 28 February; clamping the same way keeps the row from being three
 * days out for anyone who buys late in a long month.
 */
const nextChargeLabel = computed(() => {
  const now = new Date();
  const day = now.getDate();
  const anchor = new Date(now);
  // The 1st first: `setMonth` on the 31st overflows into the month after next.
  anchor.setDate(1);
  anchor.setMonth(anchor.getMonth() + 1);
  const lastOfMonth = new Date(
    anchor.getFullYear(),
    anchor.getMonth() + 1,
    0,
  ).getDate();
  anchor.setDate(Math.min(day, lastOfMonth));
  // `renewalLabel` returns null only for the checkout sentinel and an
  // unparseable string, neither of which a Date built here can be.
  return `Today, then ${renewalLabel(anchor.toISOString(), now) ?? "monthly"}`;
});

/**
 * §4.2's summary block. Held here rather than written into the template
 * because the charge row depends on which of §7.7's two outcomes this
 * purchase takes, and "Today" is false on the path where the seat joins an
 * existing subscription: nothing is charged then, the item is prorated onto
 * the next invoice, and the whole point of §7.7 is that it is one invoice.
 */
const summaryRows = computed(() => [
  { key: "Plan", value: "RevisionLab — full access" },
  { key: "Price", value: "£39 a month" },
  {
    key: "First charge",
    value: canNameHolder.value
      ? "Added to your next invoice, pro rata"
      : nextChargeLabel.value,
  },
  { key: "Cancel", value: "Any time, from your account" },
]);

/**
 * §4.2's pinned foot, under §5's one-label-per-action rule.
 *
 * "Continue to payment" is §5's label for PROCEEDING TO STRIPE, and on the
 * live-agreement path nothing proceeds: §7.7 adds a prorated item to the same
 * subscription and the seat exists the moment the call returns. A button
 * promising a payment step that never comes is the dead control
 * `api/client.ts:83` forbids, one click early — so that path gets §4.4's own
 * sentence about the shared invoice and an action that describes itself.
 */
const foot = computed(() =>
  canNameHolder.value
    ? {
        cta: "Add the seat",
        note: "Goes onto the same monthly payment, pro rata. One charge, not two.",
        arrow: false,
      }
    : {
        cta: "Continue to payment",
        note:
          "Card details are handled by Stripe. We never see them, and you come " +
          "straight back here.",
        arrow: true,
      },
);

function chooseHolder(next: string): void {
  holder.value = next === "someone_else" ? "someone_else" : "me";
  // The previous answer's outcome does not describe this one.
  errorMessage.value = null;
  showVerifyLink.value = false;
  showAskForFunding.value = false;
  promoError.value = null;
  added.value = null;
  refusalIsFinal.value = false;
}

/**
 * Enough to catch a typo before it becomes a 422, and no more. The address
 * is proved by the claim flow, not by a regex here.
 */
function looksLikeEmail(value: string): boolean {
  const at = value.indexOf("@");
  return at > 0 && at === value.lastIndexOf("@") && at < value.length - 1;
}

/**
 * The refusal described the code that was submitted, not the one being typed
 * now, so it goes as soon as the field changes.
 */
function editPromoCode(): void {
  promoError.value = null;
}

/**
 * §9.1 / §10 / §11.1 reason codes. Codes are additive and never renamed, so
 * this must not be exhaustive and must not throw: anything unlisted — a code
 * shipped by a later server than this build — is a generic failure with no
 * actions. That rule is what lets the server add codes without breaking
 * deployed clients.
 */
function applyRefusal(code: string | undefined): void {
  // Every named code below ends the attempt; only the unknown-code fallback
  // is worth pressing again. See `refusalIsFinal`.
  refusalIsFinal.value = true;
  switch (code) {
    case "email_not_verified":
      errorMessage.value = "Verify your email address before buying a seat.";
      showVerifyLink.value = true;
      return;
    case "age_below_purchase_threshold":
      // §12.1 items 4-5. A full learner account that cannot fund itself:
      // not a fault, not something to retry, and not a dead end — someone
      // else can buy the seat. Recomputed from birth month and year on
      // every attempt, so this stops being true on their 16th birthday
      // with nothing to update.
      //
      // §10 pairs this with a `request_payment` action, and as of
      // `rl-757.27` that flow exists (§9.5), so the refusal renders the ask
      // itself rather than describing a route the learner has to walk
      // outside the product. Keyed on the code and not on §10's `actions`
      // list, for the reason `api/client.ts` gives: an affordance that
      // vanishes because a server omitted it from a list is worse than one
      // chosen from the code, and here it is the ONLY route to a seat for
      // most of the cohort.
      //
      // The sentence shrinks to what the control does not say. It still
      // names the address, because the person being asked needs to
      // recognise who they are paying for.
      errorMessage.value =
        "You need to be 16 to buy a seat for yourself, so this one has to be paid for " +
        `by someone else — a parent, carer or guardian. Ask them below and we will ` +
        `send them a link. The seat comes to your email address${
          auth.user?.email ? ` (${auth.user.email})` : ""
        }.`;
      showAskForFunding.value = true;
      return;
    case "already_subscribed":
      // §11.1.8: the holder is the buyer, so naming their own subscription
      // discloses nothing they do not already own.
      errorMessage.value =
        "You already have a seat — one plan covers every subject and board. Nothing " +
        "has been charged.";
      return;
    case "holder_already_entitled":
      errorMessage.value = REFUSAL_HOLDER_ENTITLED;
      return;
    case "holder_already_invited":
      // §11.1.8's double-billing case, and unlike the refusal above this
      // one is scoped to the buyer's own agreements — it describes their
      // activity and nobody else's, so it can say plainly what happened.
      //
      // §10 pairs it with a `manage_invitation` action, and as of
      // rl-757.28 the whole of it exists: rl-757.43 shipped resend,
      // correct and cancel, and .28 shipped reassign and revoke. This
      // sentence used to end "not available yet — email support" and both
      // halves are now false, which is the same debt `seat-added-next`
      // was cleaned of when the claim flow landed. So it names the surface
      // instead of a mailbox.
      errorMessage.value =
        "You have already invited that address to a seat. Buying again would bill you " +
        "twice for the same person, so nothing has been charged. Change or cancel that " +
        "invitation under “Seats you pay for” on your account page, or use a different " +
        "address here.";
      return;
    case "funding_requires_existing_subscription":
      errorMessage.value = REFUSAL_NEEDS_OWN_PLAN;
      return;
    case "invalid_promo_code":
      // Both causes, because the server cannot tell us which: a code that
      // does not exist or has expired, and a code this customer has already
      // redeemed as far as its per-customer limit allows.
      promoError.value =
        "That promotion code could not be applied, so nothing has been charged. " +
        "Check it for typos. If you have used it before, most codes only work once " +
        "per person, so another seat needs a different code — or leave the field " +
        "empty to buy at the usual price.";
      return;
    default:
      refusalIsFinal.value = false;
      errorMessage.value = "Could not buy that seat. Try again in a moment.";
  }
}

async function buySeat(): Promise<void> {
  if (submitting.value) return;
  errorMessage.value = null;
  showVerifyLink.value = false;
  showAskForFunding.value = false;
  promoError.value = null;
  added.value = null;
  refusalIsFinal.value = false;

  const forSelf = buyingForSelf.value;
  // Only asked for, and only sent, when it can be honoured.
  const naming = !forSelf && canNameHolder.value;
  const email = holderEmail.value.trim();
  const promo = promoCode.value.trim();
  if (naming && !looksLikeEmail(email)) {
    errorMessage.value =
      "Enter the email address of the person this seat is for.";
    return;
  }

  submitting.value = true;
  try {
    // The token's email_verified claim can lag the live state right after
    // the user clicks the verification link, which would 403 the call below.
    await auth.refreshSession();
    if (!auth.isEmailVerified) {
      applyRefusal("email_not_verified");
      return;
    }
    const result = await apiClient.subscribe({
      // The intent travels even when the address cannot: an unassigned
      // seat is what "someone else" means on the Checkout path.
      seatFor: forSelf ? "self" : "someone_else",
      holderEmail: naming ? email : null,
      // Omitted rather than sent as null when empty: the field is optional
      // and the server's default already means "no discount".
      ...(promo ? { promoCode: promo } : {}),
    });
    if (result.mode === "checkout") {
      // No payment method on file yet, so Stripe collects one.
      if (result.url) {
        window.location.href = result.url;
        return;
      }
      errorMessage.value = "Could not open checkout. Try again in a moment.";
      return;
    }
    // mode === 'seat_added': §7.7 put the seat on the existing subscription
    // and it exists now. There is nothing to redirect to, so say so —
    // otherwise this is a button that visibly did nothing.
    added.value = { holderEmail: forSelf ? null : email };
    holderEmail.value = "";
    // A code is spent per seat, and re-sending the same one for the next
    // seat is the per-customer refusal above waiting to happen.
    promoCode.value = "";
    emit("purchased");
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      await router.push("/login");
      return;
    }
    if (e instanceof ApiError) {
      applyRefusal(e.code);
      return;
    }
    errorMessage.value = "Could not buy that seat. Try again in a moment.";
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <section class="seat-purchase" data-testid="seat-purchase">
    <div class="seat-main">
      <p class="seat-label">Who is this seat for?</p>
      <RlSegmented
        :model-value="holder"
        :options="HOLDER_OPTIONS"
        label="Who is this seat for?"
        class="seat-choice"
        data-testid="holder-choice"
        @update:model-value="chooseHolder"
      />
      <!-- Stated with the choice, because the answer must not read as a
           choice between two products: it is the same seat either way. -->
      <p class="seat-note">
        It is the same seat either way — full access to every subject and board.
      </p>

      <!-- §4.2's summary block. Four rows and no CTA: this is what the buyer
           is agreeing to, stated before they are asked for a card rather
           than on the receipt afterwards. -->
      <dl class="seat-summary" data-testid="seat-summary">
        <div v-for="row in summaryRows" :key="row.key" class="seat-summary-row">
          <dt>{{ row.key }}</dt>
          <dd>{{ row.value }}</dd>
        </div>
      </dl>

      <!-- Shown only when the address can be honoured (see canNameHolder).
           On the Checkout path it cannot travel, so instead of a field that
           always ends in a refusal the buyer is told what happens next. -->
      <label v-if="!buyingForSelf && canNameHolder" class="seat-field">
        Their email address
        <input
          v-model="holderEmail"
          type="email"
          autocomplete="off"
          required
          data-testid="holder-email"
        />
        <span class="seat-note">
          The seat is held for this address until they claim it.
        </span>
      </label>

      <!-- §4.2's two explainer rows — the Checkout path's answer to the same
           question, said before payment rather than after, because "who is
           this for?" invites an address and this buyer is about to be asked
           for money instead. There is nothing to type because §7.3 and the
           DPIA's P4 keep the invitee's address out of Stripe metadata, so
           the row says that rather than leaving an absence to interpret.

           WHAT IT MUST ALSO SAY, and did not until 2026-09-14: that we will
           EMAIL the invitation. This copy was written when handing over a
           link was the only thing the product could do; `rl-757.43` then
           shipped `POST /me/seats/{id}/invitation`, which sends to an
           address the payer types on their seats card and reports `emailed`
           or `link_only`. Saying only "you will get a link to hand to them"
           understates the product and reads as a limitation rather than as
           the one-step-later truth it is. The copy-link is still named,
           because it remains the guaranteed path when a send is refused
           (§9.8) and some payers would rather hand it over in person.

           The second row is PRD §11.2.9's sentence, which §7 makes mandatory
           wherever a payer sees a seat. It belongs before the money, not
           only on the seats card afterwards: what a payer can see about
           someone else is part of what they are buying. -->
      <template v-else-if="!buyingForSelf">
        <div class="seat-explainer" data-testid="claim-link-note">
          <LinkIcon class="seat-explainer-icon" :size="16" aria-hidden="true" />
          <p>
            There is no address to type here — it cannot travel through the
            payment page. Once it is paid for, you can send them an invitation
            by email from your account, or copy the link and hand it over
            yourself. Either way they claim the seat with their own free
            RevisionLab account.
          </p>
        </div>
        <div class="seat-explainer" data-testid="seat-privacy-note">
          <EyeOff class="seat-explainer-icon" :size="16" aria-hidden="true" />
          <p>
            You will see whether the seat is in use, never their work. Their
            study stays theirs.
          </p>
        </div>
      </template>

      <!-- Our own field, not Stripe's `allow_promotion_codes`. Not a styling
           preference: `allow_promotion_codes` and `discounts` are mutually
           exclusive on one Session, and `payment_method_collection:
           'if_required'` is evaluated AT SESSION CREATION — so a 100%-off
           code typed on Stripe's own page arrives too late to skip card
           collection and would silently destroy the card-free trial. The
           reasoning lives with the flag, in
           `stripe_client.create_subscription_checkout`. -->
      <aside :id="promoOfferId" class="seat-offer">
        <strong>25% off your first month.</strong>
        <p>Enter code <strong>FIRST25</strong> below.</p>
        <p class="seat-offer-terms">
          £29.25 for your first month, then £39 a month per seat. Cancel any
          time. For your first purchase only.
        </p>
      </aside>
      <label class="seat-field">
        Promotion code <span class="seat-optional">(optional)</span>
        <input
          v-model="promoCode"
          type="text"
          autocomplete="off"
          spellcheck="false"
          placeholder="Enter promo code"
          :aria-invalid="promoError !== null"
          :aria-describedby="promoError ? `${promoOfferId} seat-promo-error` : promoOfferId"
          data-testid="promo-code"
          @input="editPromoCode"
        />
        <!-- The per-seat caveat is here rather than only in the refusal: a
             payer funding a second seat needs to know a code may not stretch
             to it *before* it is refused. §4.2 shortens the hint to its first
             sentence; the rest stays because §4.4 routes a repeat payer into
             this same panel, and `invalid_promo_code` is indistinguishable
             from a typo once it arrives.

             The hint stays general because the live codes differ: FIRST25
             (the advertised offer) is first-time-only, so Stripe refuses it
             for a payer adding a second seat, and that refusal comes back as
             `invalid_promo_code` -- the "we will say so" below. INTRO4 (a
             demo code, not advertised) is deliberately not first-time-only,
             and what bounds its free seats is the card-free cap
             (`rl-757.29`). -->
        <span
          v-if="promoError"
          id="seat-promo-error"
          class="seat-field-alert"
          role="alert"
          data-testid="promo-error"
        >
          {{ promoError }}
        </span>
        <span v-else class="seat-note">
          A code applies to this seat only, so each seat takes its own. Some
          codes can be used more than once; if one will not stretch to a second
          seat we will say so.
        </span>
      </label>

      <template v-if="added">
        <p class="seat-success" role="status" data-testid="seat-added">
          {{
            added.holderEmail
              ? `Seat added for ${added.holderEmail}.`
              : "Seat added."
          }}
          It went onto your existing subscription, prorated, so it appears on
          that invoice — there is nothing else to pay now.
        </p>
        <!-- Honest about §11.1.10: paying does not activate the seat. There
             is still no invitation email (no send path, rl-757.26), so this
             names the delivery that DOES exist — the copy-link under "Seats
             you pay for" — rather than promising mail nobody sends or a
             support handover nobody needs any more. -->
        <p
          v-if="added.holderEmail"
          class="seat-note"
          data-testid="seat-added-next"
        >
          The seat is held for them until they claim it. We do not email them —
          go to <strong>Seats you pay for</strong>, get a claim link, and send
          it to them yourself.
        </p>
      </template>
    </div>

    <!-- §4.2's foot, pinned to the bottom of the panel. A refusal takes the
         CTA's place here rather than piling up under it, so the last thing on
         the surface is always the one thing left to do. -->
    <div class="seat-foot">
      <p
        v-if="errorMessage"
        class="seat-alert"
        role="alert"
        data-testid="seat-error"
      >
        {{ errorMessage }}
        <RouterLink
          v-if="showVerifyLink"
          to="/verify"
          data-testid="verify-link"
        >
          {{ ACTION_LABELS.resend }}
        </RouterLink>
      </p>

      <!-- §9.5, and the reason `age_below_purchase_threshold` is no longer
           a dead end: the refusal above says this seat has to be paid for
           by someone else, and this is how the learner asks them. It sits
           where the CTA would be, because for most of the cohort it IS the
           CTA — §12.1's threshold is 16 and the product is for 14-16s. -->
      <AskForFunding v-if="showAskForFunding" />

      <template v-if="!refusalIsFinal">
        <p class="seat-lock">{{ foot.note }}</p>
        <button
          type="button"
          class="primary seat-buy"
          :disabled="submitting"
          data-testid="buy-seat"
          @click="buySeat"
        >
          {{ submitting ? "Working…" : foot.cta }}
          <ArrowRight
            v-if="foot.arrow && !submitting"
            :size="16"
            aria-hidden="true"
          />
        </button>
      </template>
    </div>
  </section>
</template>

<style scoped>
/* `min-height: 100%` is what pins §4.2's foot: inside the slide-over's
   scrolling body the section stretches to the panel and `margin-top: auto`
   below pushes the foot down, while a short card on Account is unaffected
   because the percentage resolves against an auto height. */
.seat-purchase {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  min-height: 100%;
}

.seat-main,
.seat-foot {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
}

.seat-foot {
  margin-top: auto;
}

.seat-label {
  margin: 0;
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-xs);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--rl-fg-3);
}

.seat-note {
  margin: 0;
  font-size: var(--rl-text-base);
  color: var(--rl-fg-3);
}

/* Full width, and the segments share it equally: the two answers are one
   exclusive choice, and a content-sized control makes "Someone else" look
   like the bigger of two products. */
.seat-choice {
  width: 100%;
}

.seat-choice :deep(.segment) {
  flex: 1;
}

.seat-summary {
  margin: 0;
  padding: var(--sp-3) var(--sp-4);
  font-size: var(--rl-text-base);
  background: var(--rl-bg-muted);
  border-radius: var(--radius);
}

.seat-summary-row {
  display: flex;
  gap: var(--sp-4);
  justify-content: space-between;
  padding: var(--sp-2) 0;
  border-top: 1px solid var(--rl-border-subtle);
}

.seat-summary-row:first-child {
  padding-top: 0;
  border-top: none;
}

.seat-summary-row:last-child {
  padding-bottom: 0;
}

.seat-summary dt {
  color: var(--rl-fg-3);
}

.seat-summary dd {
  margin: 0;
  text-align: right;
  color: var(--rl-fg-1);
}

.seat-explainer {
  display: flex;
  gap: var(--sp-3);
  padding: var(--sp-3);
  border: 1px solid var(--rl-border-subtle);
  border-radius: var(--radius);
}

.seat-explainer p {
  margin: 0;
  font-size: var(--rl-text-base);
  color: var(--rl-fg-2);
}

.seat-explainer-icon {
  flex: none;
  margin-top: 2px;
  color: var(--rl-fg-3);
}

.seat-offer {
  padding: var(--sp-4);
  color: var(--rl-green-800);
  background: var(--rl-bg-subtle);
  border: 1px solid var(--rl-green-700);
  border-radius: var(--radius);
  line-height: 1.6;
}

.seat-offer p {
  margin: var(--sp-2) 0 0;
}

.seat-offer .seat-offer-terms {
  color: var(--rl-fg-2);
  font-size: var(--rl-text-sm);
}

.seat-field {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  font-size: var(--rl-text-base);
  color: var(--rl-fg-3);
}

.seat-optional {
  color: var(--rl-fg-4);
}

/* The panel-level alert's sibling, one step down: a refusal the buyer can
   fix in the field it sits under, not an outcome for the whole purchase. */
.seat-field-alert {
  font-size: var(--rl-text-base);
  color: var(--rl-danger-fg);
}

.seat-field input[aria-invalid="true"] {
  border-color: var(--rl-danger-fg);
}

.seat-lock {
  margin: 0;
  font-size: var(--rl-text-sm);
  color: var(--rl-fg-3);
}

.seat-buy {
  display: flex;
  gap: var(--sp-2);
  align-items: center;
  justify-content: center;
  width: 100%;
}

.seat-success {
  margin: 0;
  font-size: var(--rl-text-md);
  font-weight: 500;
  color: var(--rl-mint-600);
}

.seat-alert {
  margin: 0;
  font-size: var(--rl-text-md);
  color: var(--rl-danger-fg);
}
</style>
